import { useState, useEffect, useContext, useCallback, useRef, Suspense } from "react";
import { Chip } from "@material-ui/core";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import { AuthContext, UIContext } from "../../../../../contexts";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import DynamicReportEditableForm from "../../../../shared/dynamicEditableForm/DynamicEditableForm";
import { api } from "../../../../../utils/api";
import { Validation } from "../../../../../@types/validation.types";
import { trackPromise } from "react-promise-tracker";
import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { Member } from "../../../../../@types/member.types";
import JSONToExcel from "../../../../../utils/commonFunctions/JSONToExcel";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { compareInvalidDates } from "../../../../../utils/commonFunctions/date";
import { Plan, PlanStatus } from "../../../../../@types/plan.types";
import { PLAN_STATUS } from "../../../../../constants/plan";
import { BENEFIT_CHANGES } from "../../../../../constants/benefitChangesConstant";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { Group } from "../../../../../@types/group.types";
import { useEmployerRoute } from "../../../../../hooks";
import { GROUP_NUMBER } from "../../../../../constants/groups";

const chipMessage = "This report will show all the benefits added or ended between a range of dates.";

const BenefitChangesReport = (): JSX.Element => {
	const [reportDetailsDynamicForm, setReportDetailsDynamicForm] = useState<DynamicForm>();
	const { setDashboardHeader } = useContext(UIContext);
	const isEmployerRoute = useEmployerRoute();
	const queryGroupNumber = useRouteParam("gn");
	const [selectedGroup, setSelectedGroup] = useState<Group>();
	const [reportFieldDetails, setReportFieldDetails] = useState<any>({
		alternative_report_name: "",
		group_number: null,
		plan_code: "",
		start_date: "",
		end_date: "",
		type: ""
	});
	const { user } = useContext(AuthContext); // Extracting logged in user from central storage.
	const [assignedGroups, setAssignedGroups] = useState([]);
	const [plans, setPlans] = useState([]);
	const [selectedPlan, setSelectedPlan] = useState<Plan>();
	const [validation, setValidation] = useState<Validation>({
		reportFieldDetails: {},
		status: "invalid"
	});
	const _validation = useRef<Validation>();
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const handleValidation = useCallback(async () => {
		const { group_number, start_date, end_date, plan_code } = reportFieldDetails;

		_validation.current = {
			...validation,
			reportFieldDetails: {},
			status: "invalid"
		};
		let flag = true;

		// employee validation checking
		if (group_number === null) {
			_validation.current.reportFieldDetails["group_number"] = "Group is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (start_date.length === 0) {
			_validation.current.reportFieldDetails["start_date"] = "Start date is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		// if (end_date.length === 0) {
		// 	_validation.current.reportFieldDetails["end_date"] = "End date is required";
		// 	_validation.current["status"] = "invalid";
		// 	flag = false;
		// }
		if (start_date.length !== 0) {
			if (compareInvalidDates(start_date, end_date)) {
				_validation.current.reportFieldDetails["start_date"] = "Start Date cannot be greater than End Date!";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}
		if (end_date.length !== 0) {
			if (compareInvalidDates(start_date, end_date)) {
				_validation.current.reportFieldDetails["end_date"] = "End Date cannot be less than Start Date!";
				_validation.current["status"] = "invalid";
				flag = false;
			}
		}
		if (plan_code.length === 0) {
			_validation.current.reportFieldDetails["plan_code"] = "Plan is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		// if (type.length === 0) {
		// 	_validation.current.reportFieldDetails["type"] = "Report Type is required";
		// 	_validation.current["status"] = "invalid";
		// 	flag = false;
		// }

		if (flag === true) _validation.current["status"] = "valid";
		else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [reportFieldDetails, validation]);

	const getAssignGroups = useCallback(async () => {
		const params = {
			user_id: user?._id
		};
		const response = await api.assignGroupsAndLocation.getAssignedGroupsAndLocations(params);

		if (response) {
			const _assignedGroups: any[] = [];
			response.groupsAndLocations.forEach((group: any) => {
				if (group.assigned_group_info) {
					_assignedGroups.push(group);
				}
			});

			setAssignedGroups(Object.assign([], _assignedGroups));
		}
	}, [user?._id]);

	const getPlans = useCallback(async () => {
		if (reportFieldDetails.group_number) {
			const { group_number } = reportFieldDetails;
			const planStatus: PlanStatus | string = PLAN_STATUS.active;
			const query = {
				status: planStatus,
				group_number
			};
			const _plans = await api.plan.getPlans(query);
			// console.log("Plans", _plans);
			setPlans(Object.assign([], _plans));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reportFieldDetails.group_number]);

	const handleSelectGroup = useCallback(
		(queryGroupNumber: number) => {
			// // console.log("Query Group Number", queryGroupNumber);

			assignedGroups.forEach((assignedGroup: Group) => {
				if (assignedGroup.group_number === queryGroupNumber) {
					setSelectedGroup(Object.assign({}, assignedGroup));
					setReportFieldDetails(
						Object.assign({}, reportFieldDetails, {
							group_number: queryGroupNumber
						})
					);
				}
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[assignedGroups]
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;

			if (name === "plan_code") {
				plans.forEach((plan: Plan) => {
					if (value === plan.plan_code) setSelectedPlan(Object.assign({}, plan));
				});

				setReportFieldDetails(Object.assign({}, reportFieldDetails, { plan_code: value }));
			} else setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: value }));
		},
		[plans, reportFieldDetails]
	);

	const handleDateChange = useCallback(
		(_date: MaterialUiPickersDate, name: string) => {
			const date = new Date(_date as Date);
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: parsedDate }));
		},
		[reportFieldDetails]
	);

	const handleClearDateField = useCallback(
		(event: any, name: string) => {
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { [name]: null }));
		},
		[reportFieldDetails]
	);

	const handleGenerateReportSubmit = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			try {
				event.preventDefault();
				const validation = await handleValidation();
				if (validation === "valid") {
					// console.log("Benefit Changes Report Generation");
					const { group_number, type, start_date, end_date } = reportFieldDetails;
					let { plan_code } = reportFieldDetails;
					const startDate = new Date(start_date);
					const endDate = new Date(end_date);

					let sub_plan: string | null = null;
					if (group_number === GROUP_NUMBER.PANHANDLE_FIRST_BANK && plan_code.includes("Health Plan")) {
						// sub_plan = plan_code.split("(")[1].split(")")[0];

						const match = plan_code.match(/^([^(]+)\(([^)]+)\)$/);

						plan_code = match?.[1].trim() || ""; // "Health Plan"
						sub_plan = match?.[2].trim() || ""; // "High Deductible Health Plan"
					}

					const payload = {
						group_number,
						plan_name: plan_code,
						sub_plan: sub_plan,
						type: "Benefit Changes with Termination",
						start_date:
							startDate.getFullYear() + "-" + (startDate.getMonth() + 1) + "-" + startDate.getDate(),
						end_date:
							end_date !== ""
								? endDate.getFullYear() + "-" + (endDate.getMonth() + 1) + "-" + endDate.getDate()
								: null
					};

					// console.log("Payload", payload);
					const response: Member = await trackPromise(api.reports.benefitChangesReport(payload));

					if (response) {
						// console.log("Response", response);

						const fileName = reportFieldDetails.alternative_report_name
							? reportFieldDetails.alternative_report_name
							: "Benefit Changes Report";

						const excelPayload = {
							user: {
								role: user?.role,
								name: user?.first_name + " " + user?.last_name
							},
							reportFieldDetails,
							response
						};

						await trackPromise(JSONToExcel(excelPayload, fileName));

						setTimeout(() => {
							setSnackbarAPICallProps(
								Object.assign({}, snackbarAPICallProps, {
									open: true,
									message: "Benefit Changes Report Fetched Successfully and File Downloaded!",
									severity: "success"
								})
							);
						}, 2000);
					}
				}
			} catch (error) {
				alert("Benefit Changes Report Generation Unsuccessful!");
				throw error;
			}
		},
		[handleValidation, reportFieldDetails, user, snackbarAPICallProps]
	);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.benefit_changes_report);
		getAssignGroups();
		getPlans();
	}, [getAssignGroups, getPlans, setDashboardHeader]);

	useEffect(() => {
		if (isEmployerRoute && queryGroupNumber) handleSelectGroup(queryGroupNumber as number);
	}, [isEmployerRoute, queryGroupNumber, handleSelectGroup]);

	useEffect(() => {
		if (reportFieldDetails.start_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { start_date: "" }));
		if (reportFieldDetails.end_date === null)
			setReportFieldDetails(Object.assign({}, reportFieldDetails, { end_date: "" }));
	}, [reportFieldDetails]);

	useEffect(() => {
		setReportDetailsDynamicForm(
			Object.assign(
				{},
				{
					"Benefit Changes Report Generation": [
						{
							label: "Group",
							name: "group_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value:
								isEmployerRoute && queryGroupNumber
									? selectedGroup?.name
									: reportFieldDetails.group_number,
							type: isEmployerRoute && queryGroupNumber ? "textfield" : "select",
							options: [...assignedGroups]
						},
						{
							label: "Plan",
							name: "plan_code",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							value: reportFieldDetails.plan_code,
							type: "select",
							options: [...plans]
						},
						// {
						// 	label: "Type",
						// 	name: "type",
						// 	onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
						// 	value: reportFieldDetails.type,
						// 	type: "select",
						// 	options: BENEFIT_CHANGES
						// },
						{
							label: "Start Date",
							name: "start_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateChange(date, name),
							placeholder: "Enter start date",
							value: reportFieldDetails.start_date,
							type: "date"
						},
						{
							label: "End Date",
							name: "end_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateChange(date, name),
							placeholder: "Enter end date",
							value: reportFieldDetails.end_date,
							type: "date"
						},
						{
							label: "Alternative Report Name",
							name: "alternative_report_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleChange(event),
							placeholder: "Enter alternative report name",
							value: reportFieldDetails.alternative_report_name,
							type: "textfield"
						}
					]
				}
			)
		);
	}, [
		assignedGroups,
		reportFieldDetails,
		handleChange,
		handleDateChange,
		plans,
		isEmployerRoute,
		queryGroupNumber,
		selectedGroup?.name
	]);

	// console.log("reportFieldDetails", reportFieldDetails);

	return (
		<div className="census-report-container" id="census-report-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="chip-container" id="chip-container">
				<Chip className="chip-green" label={chipMessage} variant="outlined" />
			</div>
			<DynamicReportEditableForm
				reportDetailsDynamicForm={reportDetailsDynamicForm}
				// selectedPlanName={selectedPlan?.plan_name}
				handleClearDateField={handleClearDateField}
				validation={validation}
				handleGenerateReportSubmit={handleGenerateReportSubmit}
			/>
		</div>
	);
};

export default BenefitChangesReport;
